import { useState, type ReactElement, type ReactNode } from 'react'
import ExpandLess from '@mui/icons-material/ExpandLess'
import ExpandMore from '@mui/icons-material/ExpandMore'
import { Box, IconButton, Typography, useTheme } from '@mui/material'

export type SectionProps = {
  title: string
  collapsible?: boolean
  defaultOpen?: boolean
  /**
   * Controlled open state. When provided (together with `onOpenChange`), the section defers to the
   * caller instead of tracking its own state — use this to persist collapse state externally (e.g.
   * across a remount). Omit both to keep the section uncontrolled (`defaultOpen` only).
   */
  open?: boolean
  onOpenChange?: (open: boolean) => void
  /** When set, the section has no bottom divider. Default: false (divider shown). */
  hideBorderBottom?: boolean
  /** ui2 styling: gives the title row a light grey background. Default: false. */
  ui2?: boolean
  summary?: ReactNode
  /** Rendered directly after the title text (e.g. a save/autosave indicator). */
  titleAdornment?: ReactNode
  /** Right-aligned controls in the title row, before the collapse toggle. */
  actions?: ReactNode
  /** Lets the actions wrap below the title when the title row is too narrow. Default: false. */
  wrapActions?: boolean
  children?: ReactNode
}

/**
 * Titled, optionally collapsible section. Carries no outer padding of its own (only the internal
 * title→content gap) — the outer container owns the gutters, top/bottom padding, and the spacing
 * between stacked sections. Without children it is just its title row (e.g. a title with actions).
 */
export const Section = ({
  title,
  collapsible,
  defaultOpen = true,
  open: controlledOpen,
  onOpenChange,
  hideBorderBottom = false,
  ui2 = false,
  summary,
  titleAdornment,
  actions,
  wrapActions = false,
  children,
}: SectionProps): ReactElement => {
  const theme = useTheme()
  const [uncontrolledOpen, setUncontrolledOpen] = useState(defaultOpen)
  const open = controlledOpen ?? uncontrolledOpen
  const toggleOpen = () => {
    const next = !open
    onOpenChange?.(next)
    if (controlledOpen === undefined) setUncontrolledOpen(next)
  }
  const hasContent = children !== undefined && children !== null
  const borderBottomSx = hideBorderBottom
    ? undefined
    : { borderBottom: `1px solid ${theme.palette.divider}` }
  const titleRowSx = ui2
    ? {
        backgroundColor: theme.palette.grey[100],
        px: 1.5,
        minHeight: 35,
        mb: hasContent ? 1 : undefined,
      }
    : { pb: hasContent ? 0.75 : undefined }
  return (
    <Box
      sx={{
        flexShrink: 0,
        background: theme.palette.common.white,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexWrap: wrapActions ? 'wrap' : undefined,
          alignItems: 'center',
          gap: 1.25,
          ...titleRowSx,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
          <Typography
            sx={{
              fontSize: 12,
              fontWeight: 500,
              color: theme.palette.text.primary,
              textTransform: 'uppercase',
              letterSpacing: '0.06em',
              flexShrink: 0,
            }}
          >
            {title}
          </Typography>
          {titleAdornment}
        </Box>
        <Box
          sx={{
            ml: 'auto',
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            minWidth: 0,
          }}
        >
          {summary && collapsible && !open ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0 }}>
              {summary}
            </Box>
          ) : null}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
            {actions}
            {collapsible ? (
              <IconButton size="small" onClick={toggleOpen}>
                {open ? <ExpandLess sx={{ fontSize: 18 }} /> : <ExpandMore sx={{ fontSize: 18 }} />}
              </IconButton>
            ) : null}
          </Box>
        </Box>
      </Box>
      {!hasContent ? null : !collapsible || open ? (
        <Box sx={{ ...borderBottomSx, px: ui2 ? 0.5 : undefined }}>{children}</Box>
      ) : hideBorderBottom ? null : (
        <Box sx={{ borderBottom: `1px solid ${theme.palette.divider}` }} />
      )}
    </Box>
  )
}
